'use client';

import React from 'react';
import { Box, Typography, Button, Container, Stack } from '@mui/material';
import { ErrorOutline, Home, Refresh } from '@mui/icons-material';

// Class component to catch errors
class GenericErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[GenericErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f9fafb',
            }}
          >
            <Container maxWidth="sm">
              <Stack spacing={3} alignItems="center" textAlign="center">
                <ErrorOutline sx={{ fontSize: 80, color: '#ef4444' }} />
                <Typography variant="h4" fontWeight={700} color="#111827">
                  Oops! Something went wrong
                </Typography>
                <Typography variant="body1" color="#6b7280">
                  We're sorry, but something unexpected happened. Please try refreshing the page or
                  return to the home page.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => window.location.reload()}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Refresh Page
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Home />}
                    onClick={() => (window.location.href = '/')}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 3,
                      bgcolor: '#000',
                      '&:hover': { bgcolor: '#1a1a1a' },
                    }}
                  >
                    Go Home
                  </Button>
                </Stack>
              </Stack>
            </Container>
          </Box>
        )
      );
    }
    return this.props.children;
  }
}

// Wrapper functional component
function GenericErrorBoundary({ children, fallback }) {
  return <GenericErrorBoundaryClass fallback={fallback}>{children}</GenericErrorBoundaryClass>;
}

export default GenericErrorBoundary;
